import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Spin } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  InboxOutlined,
  DollarCircleOutlined,
  WarningOutlined,
  TrophyOutlined,
  RightOutlined,
  SmileOutlined,
  WalletOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { PATH } from "../../redux/utils";
import { getEmpresaConfig } from "../../components/utils/empresaConfig";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip);
dayjs.locale("es");

const money = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });

function Home() {
  const navigate = useNavigate();
  const today = useMemo(() => dayjs(), []);
  const [empresa, setEmpresa] = useState({ nombre: "" });
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchResumen = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${PATH}/dashboard/resumen`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResumen(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getEmpresaConfig().then((c) => setEmpresa(c || {}));
    fetchResumen();
  }, [fetchResumen]);

  const kpis = resumen?.kpis || {};
  const operacion = resumen?.operacion || {};
  const alertas = resumen?.alertas || { cuentas_por_cobrar: [], stock_bajo: [] };
  const insights = resumen?.insights || { top_productos: [], ventas_7_dias: [] };

  const comparativo = kpis.comparativo_pct;
  const sinComparativo = comparativo === null || comparativo === undefined;
  const trendIcon = sinComparativo || comparativo === 0
    ? <MinusOutlined />
    : comparativo > 0
    ? <ArrowUpOutlined />
    : <ArrowDownOutlined />;
  const trendClass = sinComparativo || comparativo === 0
    ? "hm-kpi-trend--neutral"
    : comparativo > 0
    ? "hm-kpi-trend--up"
    : "hm-kpi-trend--down";

  const chartData = {
    labels: (insights.ventas_7_dias || []).map((d) => dayjs(d.dia).format("ddd D")),
    datasets: [
      {
        label: "Ventas",
        data: (insights.ventas_7_dias || []).map((d) => Number(d.ventas)),
        backgroundColor: "#f90336",
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${money(ctx.raw)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 } } },
      y: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        ticks: { font: { size: 11 }, callback: (v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`) },
      },
    },
  };

  const corteTxt = operacion.corte_abierto ? "Abierto" : operacion.corte ? "Cerrado" : "Sin abrir";
  const cuentas = alertas.cuentas_por_cobrar || [];
  const stockBajo = alertas.stock_bajo || [];
  const top = insights.top_productos || [];

  return (
    <>
      <main className="hm-main">
        <div className="hm-content">
          {/* ── BIENVENIDA ─────────────────────────────── */}
          <section className="hm-welcome">
            <div className="hm-welcome-left">
              {empresa.nombre && <div className="hm-eyebrow">{empresa.nombre}</div>}
              <div className="hm-greeting">Panel de Inicio</div>
              <div className="hm-date">{today.format("dddd, D [de] MMMM [de] YYYY")}</div>
            </div>
          </section>

          {loading ? (
            <div className="hm-card">
              <div className="hm-center">
                <Spin size="large" />
              </div>
            </div>
          ) : (
            <>
              {/* ── KPIs DEL DÍA ─────────────────────────── */}
              <section className="hm-kpi-row">
                <div className="hm-kpi-card">
                  <div className="hm-kpi-label">Ventas de hoy</div>
                  <div className="hm-kpi-value">{money(kpis.ventas_hoy)}</div>
                </div>
                <div className="hm-kpi-card">
                  <div className="hm-kpi-label">Tickets cerrados hoy</div>
                  <div className="hm-kpi-value">{kpis.tickets_hoy ?? 0}</div>
                </div>
                <div className="hm-kpi-card">
                  <div className="hm-kpi-label">Ticket promedio</div>
                  <div className="hm-kpi-value">{money(kpis.ticket_promedio)}</div>
                </div>
                <div className="hm-kpi-card">
                  <div className="hm-kpi-label">Vs. mismo día semana pasada</div>
                  <div className={`hm-kpi-value hm-kpi-trend ${trendClass}`}>
                    {trendIcon}
                    <span>{sinComparativo ? "Sin datos" : `${Math.abs(comparativo).toLocaleString("es-MX")}%`}</span>
                  </div>
                </div>
              </section>

              {/* ── ESTADO OPERATIVO ─────────────────────── */}
              <section className="hm-op-row">
                <div className="hm-op-card">
                  <WalletOutlined className="hm-op-icon hm-op-icon--corte" />
                  <div className="hm-op-body">
                    <div className="hm-op-value">{corteTxt}</div>
                    <div className="hm-op-label">Corte de caja</div>
                  </div>
                </div>
                <div className="hm-op-card">
                  <ShoppingOutlined className="hm-op-icon hm-op-icon--gastos" />
                  <div className="hm-op-body">
                    <div className="hm-op-value">{money(operacion.gastos_hoy)}</div>
                    <div className="hm-op-label">Gastos de hoy</div>
                  </div>
                </div>
                <div className="hm-op-card">
                  <InboxOutlined className="hm-op-icon hm-op-icon--stock" />
                  <div className="hm-op-body">
                    <div className="hm-op-value">{operacion.productos_stock_bajo ?? 0}</div>
                    <div className="hm-op-label">Productos con stock bajo</div>
                  </div>
                </div>
              </section>

              {/* ── ALERTAS ──────────────────────────────── */}
              <section className="hm-alerts-row">
                <div className="hm-card hm-alert-card">
                  <div className="hm-card-header">
                    <div className="hm-card-title">
                      <DollarCircleOutlined style={{ color: "#15803d" }} />
                      Cuentas por cobrar
                    </div>
                    {cuentas.length > 0 && (
                      <span className="hm-card-badge hm-card-badge--danger">{cuentas.length}</span>
                    )}
                  </div>
                  {cuentas.length > 0 ? (
                    <div className="hm-alert-list">
                      {cuentas.map((v) => (
                        <button
                          key={v.id_venta}
                          type="button"
                          className="hm-alert-row"
                          onClick={() => navigate("/app/ventas")}
                        >
                          <span className="hm-alert-name">{v.cliente || `Venta #${v.id_venta}`}</span>
                          <span className="hm-alert-meta">Saldo {money(v.saldo_pendiente)}</span>
                          <RightOutlined className="hm-alert-arrow" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="hm-empty hm-empty--small">
                      <SmileOutlined className="hm-empty-icon" />
                      <span>Sin cuentas pendientes de cobro</span>
                    </div>
                  )}
                </div>

                <div className="hm-card hm-alert-card">
                  <div className="hm-card-header">
                    <div className="hm-card-title">
                      <WarningOutlined style={{ color: "#d97706" }} />
                      Stock bajo
                    </div>
                    {stockBajo.length > 0 && (
                      <span className="hm-card-badge hm-card-badge--warn">{stockBajo.length}</span>
                    )}
                  </div>
                  {stockBajo.length > 0 ? (
                    <div className="hm-alert-list">
                      {stockBajo.map((p, i) => (
                        <button
                          key={i}
                          type="button"
                          className="hm-alert-row"
                          onClick={() => navigate("/app/productos")}
                        >
                          <span className="hm-alert-name">{p.producto}</span>
                          <span className="hm-alert-meta">{p.clave} · Existencia {p.existencia}</span>
                          <RightOutlined className="hm-alert-arrow" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="hm-empty hm-empty--small">
                      <SmileOutlined className="hm-empty-icon" />
                      <span>Sin alertas de inventario</span>
                    </div>
                  )}
                </div>
              </section>

              {/* ── INSIGHTS ─────────────────────────────── */}
              <section className="hm-insights-row">
                <div className="hm-card hm-top-card">
                  <div className="hm-card-header">
                    <div className="hm-card-title">
                      <TrophyOutlined style={{ color: "#f90336" }} />
                      Top 5 productos (7 días)
                    </div>
                  </div>
                  {top.length > 0 ? (
                    <div className="hm-top-list">
                      {top.map((p, i) => (
                        <div key={i} className="hm-top-row">
                          <span className="hm-top-rank">{i + 1}</span>
                          <span className="hm-top-name">{p.nombre}</span>
                          <span className="hm-top-qty">{p.cantidad_vendida}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="hm-empty hm-empty--small">
                      <SmileOutlined className="hm-empty-icon" />
                      <span>Sin ventas registradas en este periodo</span>
                    </div>
                  )}
                </div>

                <div className="hm-card hm-chart-card">
                  <div className="hm-card-header">
                    <div className="hm-card-title">Ventas últimos 7 días</div>
                  </div>
                  <div className="hm-chart-body">
                    <Bar data={chartData} options={chartOptions} />
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </main>

      <style>{CSS}</style>
    </>
  );
}

const CSS = `
/* ── layout ── */
.hm-main {
  background: #e7e7e7;
  min-height: calc(100vh - 56px);
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
}
.hm-content { width: 100%; display: flex; flex-direction: column; gap: 24px; flex: 1; min-height: 0; }

/* ── bienvenida ── */
.hm-welcome {
  display: flex; align-items: flex-end; justify-content: space-between; gap: 16px;
  background: #fff; border-radius: 18px; padding: 32px 36px;
  box-shadow: 0 3px 14px rgba(15,34,58,0.07); border: 1px solid #e4e8f0;
}
.hm-eyebrow { font-size: 12px; font-weight: 700; color: #f90336; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 4px; }
.hm-greeting { font-size: 30px; font-weight: 700; color: #3d0011; line-height: 1.2; letter-spacing: -0.3px; }
.hm-date { font-size: 13px; color: #f90336; margin-top: 4px; text-transform: capitalize; }

/* ── card genérico ── */
.hm-card {
  background: #fff; border-radius: 18px; border: 1px solid #e4e8f0;
  box-shadow: 0 3px 14px rgba(15,34,58,0.07); overflow: hidden;
  display: flex; flex-direction: column;
}
.hm-card-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px 12px; border-bottom: 1px solid #f0f2f6; }
.hm-card-title { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 15px; color: #3d0011; }
.hm-card-badge { font-size: 12px; font-weight: 700; color: #7a7f87; background: #f3f5f8; padding: 2px 10px; border-radius: 999px; }
.hm-card-badge--warn { color: #b45309; background: #fffbeb; }
.hm-card-badge--danger { color: #b91c1c; background: #fef2f2; }

/* ── KPIs del día ── */
.hm-kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
.hm-kpi-card {
  background: #fff; border-radius: 16px; border: 1px solid #e4e8f0;
  box-shadow: 0 3px 14px rgba(15,34,58,0.06); padding: 18px 20px;
  display: flex; flex-direction: column; gap: 8px;
}
.hm-kpi-label { font-size: 12.5px; font-weight: 600; color: #7a7f87; }
.hm-kpi-value { font-size: 24px; font-weight: 800; color: #3d0011; }
.hm-kpi-trend { display: flex; align-items: center; gap: 6px; font-size: 20px; }
.hm-kpi-trend span { font-size: 20px; }
.hm-kpi-trend--up { color: #15803d; }
.hm-kpi-trend--down { color: #b91c1c; }
.hm-kpi-trend--neutral { color: #9aa2b1; }

/* ── estado operativo ── */
.hm-op-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.hm-op-card {
  background: #fff; border-radius: 16px; border: 1px solid #e4e8f0;
  box-shadow: 0 3px 14px rgba(15,34,58,0.06); padding: 18px 20px;
  display: flex; align-items: center; gap: 14px;
}
.hm-op-icon { font-size: 26px; flex-shrink: 0; }
.hm-op-icon--corte { color: #f90336; }
.hm-op-icon--gastos { color: #d97706; }
.hm-op-icon--stock { color: #f90336; }
.hm-op-value { font-size: 22px; font-weight: 800; color: #3d0011; line-height: 1.1; }
.hm-op-label { font-size: 12.5px; color: #7a7f87; font-weight: 600; }

/* ── alertas ── */
.hm-alerts-row { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.hm-alert-card { min-height: 220px; }
.hm-alert-list { display: flex; flex-direction: column; overflow-y: auto; max-height: 260px; }
.hm-alert-row {
  display: flex; align-items: center; gap: 10px; padding: 11px 20px; border: none;
  background: transparent; cursor: pointer; text-align: left; width: 100%;
  border-bottom: 1px solid #f5f6f8; transition: background 0.13s;
}
.hm-alert-row:last-child { border-bottom: none; }
.hm-alert-row:hover { background: #f7f9fc; }
.hm-alert-name { font-size: 13px; font-weight: 600; color: #3d0011; flex-shrink: 0; max-width: 40%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hm-alert-meta { font-size: 12px; color: #8a93a2; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hm-alert-arrow { color: #c4cad6; font-size: 11px; flex-shrink: 0; }

/* ── insights ── */
.hm-insights-row { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.hm-top-card, .hm-chart-card { min-height: 300px; }
.hm-top-list { display: flex; flex-direction: column; padding: 6px 0; }
.hm-top-row { display: flex; align-items: center; gap: 12px; padding: 10px 20px; border-bottom: 1px solid #f5f6f8; }
.hm-top-row:last-child { border-bottom: none; }
.hm-top-rank {
  width: 22px; height: 22px; border-radius: 50%; background: #eef4ff; color: #f90336;
  font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.hm-top-name { font-size: 13px; font-weight: 600; color: #3d0011; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hm-top-qty { font-size: 12.5px; font-weight: 700; color: #f90336; background: #eef4ff; padding: 2px 10px; border-radius: 999px; }
.hm-chart-body { flex: 1; padding: 16px 20px 20px; min-height: 220px; position: relative; }

/* ── vacío / cargando ── */
.hm-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 36px 20px; color: #b0b8c6; font-size: 13px; flex: 1; }
.hm-empty--small { padding: 24px 20px; }
.hm-empty-icon { font-size: 28px; color: #d0d8e6; }
.hm-center { display: flex; align-items: center; justify-content: center; padding: 60px; flex: 1; }

/* ── responsive ── */
@media (max-width: 1100px) {
  .hm-kpi-row { grid-template-columns: repeat(2, 1fr); }
  .hm-op-row { grid-template-columns: 1fr; }
}
@media (max-width: 860px) {
  .hm-alerts-row, .hm-insights-row { grid-template-columns: 1fr; }
}
@media (max-width: 600px) {
  .hm-main { padding: 16px; }
  .hm-welcome { padding: 24px 20px; }
  .hm-greeting { font-size: 22px; }
  .hm-kpi-row { grid-template-columns: 1fr; }
}
`;

export default Home;
